/**
 * Supabase data source provider.
 *
 * Reads meetings, conversations and writes updates through the proxy Cloud
 * Function rather than talking to Supabase from the browser. The anon key that
 * used to serve these queries ships inside the built bundle, so it is public;
 * the proxy holds the service-role key instead and constrains every query to
 * the accounts the caller's login token grants. Row shapes are unchanged, so
 * fieldMapper still owns all mapping.
 */
import { DataSource, SupabaseMeetingWithLead, SupabaseMessageRow } from './types';
import { Meeting, ProcessedMessage } from '../../shared/types';
import { supabaseRowToMeeting, splitFieldsForUpdate, supabaseMessageToProcessed } from './fieldMapper';
import { authService } from '../authService';
import { csrfService } from '../csrfService';

// Server caps a page at 500 (inbox_data.MAX_MEETINGS_PAGE). Stay under it and
// page until a short response, so an account outgrowing the cap can't be
// silently truncated the way a single .range(30, 9999) would have been.
const MEETINGS_PAGE = 250;
const MEETINGS_INITIAL = 30;

// Conversation preload cache (keyed by lead_id)
const conversationCache = new Map<string, {
  messages: ProcessedMessage[];
  conversationSubject: string;
}>();

export async function inboxRequest<T>(
  path: string,
  body: Record<string, unknown>,
  method: 'POST' | 'PATCH' = 'POST',
): Promise<T> {
  const accessToken = authService.getAccessToken();
  if (!accessToken) {
    throw new Error('No authentication token available. Please log in again.');
  }

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };

  // The proxy enforces CSRF on the write only; the reads are POST purely
  // because the conversation batch carries too many ids for a query string.
  if (method === 'PATCH') {
    Object.assign(headers, await csrfService.getTokenHeaders());
  }

  const response = await fetch(`${import.meta.env.VITE_AIRTABLE_PROXY_URL}${path}`, {
    method,
    credentials: 'same-origin',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    throw new Error(detail.error || `Inbox request failed (${response.status})`);
  }

  return response.json() as Promise<T>;
}

export class SupabaseProvider implements DataSource {
  private async fetchMeetingsPage(accountName: string, offset: number, limit: number): Promise<Meeting[]> {
    const { meetings } = await inboxRequest<{ meetings: SupabaseMeetingWithLead[] }>(
      '/inbox/meetings',
      { accountName, offset, limit },
    );
    return (meetings || []).map(supabaseRowToMeeting);
  }

  async fetchMeetingsProgressive(accountName: string): Promise<{
    initialMeetings: Meeting[];
    allMeetingsPromise: Promise<Meeting[]>;
  }> {
    // Phase 1: first page (fast)
    const initialMeetings = await this.fetchMeetingsPage(accountName, 0, MEETINGS_INITIAL);

    // Phase 2: the remainder, in the background
    const allMeetingsPromise = (async (): Promise<Meeting[]> => {
      const rest: Meeting[] = [];
      let offset = MEETINGS_INITIAL;
      try {
        for (;;) {
          const page = await this.fetchMeetingsPage(accountName, offset, MEETINGS_PAGE);
          rest.push(...page);
          if (page.length < MEETINGS_PAGE) break;
          offset += MEETINGS_PAGE;
        }
      } catch (error) {
        console.error('Failed to fetch remaining meetings:', (error as Error).message);
        return [];
      }
      return rest;
    })();

    return { initialMeetings, allMeetingsPromise };
  }

  async updateMeeting(meetingId: string, fields: Partial<Meeting['fields']>): Promise<void> {
    const { meetingUpdates, leadUpdates } = splitFieldsForUpdate(fields);

    if (Object.keys(meetingUpdates).length === 0 && Object.keys(leadUpdates).length === 0) {
      return;
    }

    // The server re-reads the meeting under the account constraint to authorise
    // the write and to resolve lead_id, so both halves go in one call.
    await inboxRequest<{ meeting_updated: boolean; lead_updated: boolean }>(
      '/inbox/meeting',
      { meetingId, meetingUpdates, leadUpdates },
      'PATCH',
    );
  }

  private cacheConversations(byLead: Record<string, SupabaseMessageRow[]>, leadIds: string[]): void {
    for (const leadId of leadIds) {
      const leadMessages = byLead[leadId] || [];
      const processed = leadMessages.map(supabaseMessageToProcessed);
      const subject = processed.length > 0 ? processed[0].subject : '';
      conversationCache.set(leadId, { messages: processed, conversationSubject: subject });
    }
  }

  async preloadConversations(meetings: Meeting[], tier: 'ultra-high' | 'high' | 'remaining'): Promise<void> {
    let targetMeetings: Meeting[];
    switch (tier) {
      case 'ultra-high':
        targetMeetings = meetings.slice(0, 3);
        break;
      case 'high':
        targetMeetings = meetings.slice(3, 10);
        break;
      case 'remaining':
        targetMeetings = meetings.slice(10);
        break;
    }

    if (targetMeetings.length === 0) return;

    // Collect all lead_ids from the target meetings
    const leadIds = targetMeetings
      .map(m => m.fields['Link to Missatges (from Link Inbox)']?.[0])
      .filter((id): id is string => !!id);

    if (leadIds.length === 0) return;

    // Batch fetch all messages for these leads in one call (the server chunks
    // the id list internally so the 'remaining' tier stays one round trip).
    try {
      const { byLead } = await inboxRequest<{ byLead: Record<string, SupabaseMessageRow[]> }>(
        '/inbox/conversations',
        { leadIds },
      );
      this.cacheConversations(byLead || {}, leadIds);
    } catch (error) {
      console.error(`Failed to preload conversations (${tier}):`, (error as Error).message);
    }
  }

  async getConversation(messageRecordIds: string[], leadName?: string): Promise<{
    messages: ProcessedMessage[];
    conversationSubject: string;
  }> {
    // In Supabase mode, messageRecordIds[0] is actually the lead_id
    const leadId = messageRecordIds?.[0];
    if (!leadId) {
      return { messages: [], conversationSubject: '' };
    }

    // Check cache first
    const cached = conversationCache.get(leadId);
    if (cached) return cached;

    const { byLead } = await inboxRequest<{ byLead: Record<string, SupabaseMessageRow[]> }>(
      '/inbox/conversations',
      { leadIds: [leadId] },
    );

    const processed = (byLead?.[leadId] || []).map(supabaseMessageToProcessed);
    const subject = processed.length > 0 ? processed[0].subject : '';
    const result = { messages: processed, conversationSubject: subject };

    // Cache it
    conversationCache.set(leadId, result);
    return result;
  }

  async getPreloadedConversation(messageRecordIds: string[]): Promise<{
    messages: ProcessedMessage[];
    conversationSubject: string;
  } | null> {
    const leadId = messageRecordIds?.[0];
    if (!leadId) return null;
    return conversationCache.get(leadId) || null;
  }

  clearConversationCache(): void {
    conversationCache.clear();
  }
}
